import {
    Directive,
    HostListener,
    ComponentRef,
    ViewContainerRef,
    Input,
    ComponentFactoryResolver
} from '@angular/core';
import { TooltipContentComponent } from './TooltipContent.component';

@Directive({
    selector: '[tooltip]',
    exportAs: 'tooltip',
    standalone: false
})
export class TooltipDirective {
    @Input()
    tooltipContent: string | TooltipContentComponent;
    @Input()
    tooltipDisabled: boolean;
    @Input()
    tooltipAnimation = true;
    @Input()
    tooltipPlacement: 'top' | 'bottom' | 'left' | 'right' = 'bottom';
    @Input()
    tooltipDelay = 0;

    /**
     * If set to true, the tooltip will close when the user's mouse leaves the
     * tooltip content, rather than the component this directive is applied to.
     */
    @Input()
    hideWhenLeavingTooltipContent = false;

    private tooltip: ComponentRef<TooltipContentComponent>;
    private visible: boolean;
    private internalTimer: number;
    private tooltipContentFocusEntered = false;

    constructor(
        private viewContainerRef: ViewContainerRef,
        private resolver: ComponentFactoryResolver
    ) {}

    @HostListener('focusin')
    @HostListener('mouseenter')
    show(): void {
        if (this.tooltipDisabled) {
            this.hide();
        }
        if (this.tooltipDisabled || this.visible) {
            return;
        }

        this.visible = true;

        this.internalTimer = window.setTimeout(() => {
            if (!this.tooltipContent) {
                throw new Error(
                    'this.tooltipContent is undefined/null. We need to handle this.'
                );
            }
            if (typeof this.tooltipContent === 'string') {
                const factory = this.resolver.resolveComponentFactory(
                    TooltipContentComponent
                );
                if (!this.visible) {
                    return;
                }

                this.tooltip = this.viewContainerRef.createComponent(factory);
                this.tooltip.instance.hostElement =
                    this.viewContainerRef.element.nativeElement;
                this.tooltip.instance.content = this.tooltipContent as string;
                this.tooltip.instance.placement = this.tooltipPlacement;
                this.tooltip.instance.animation = this.tooltipAnimation;
            } else {
                const tooltip = this.tooltipContent as TooltipContentComponent;
                tooltip.hostElement =
                    this.viewContainerRef.element.nativeElement;
                tooltip.placement = this.tooltipPlacement;
                tooltip.animation = this.tooltipAnimation;
                if (this.hideWhenLeavingTooltipContent) {
                    tooltip.focusEnterEvent.subscribe(() => {
                        this.handleTooltipContentFocusEnter();
                    });
                    tooltip.focusLeaveEvent.subscribe(() => {
                        this.handleTooltipContentFocusLeave();
                    });
                }
                tooltip.show();
            }
        }, this.tooltipDelay);
    }

    @HostListener('focusout')
    @HostListener('mouseleave')
    hide(): void {
        if (!this.visible) {
            return;
        }

        this.visible = false;

        clearTimeout(this.internalTimer);

        if (this.tooltip) {
            this.tooltip.destroy();
        }

        if (this.tooltipContent instanceof TooltipContentComponent) {
            const tooltip = this.tooltipContent;
            if (!this.hideWhenLeavingTooltipContent) {
                tooltip.hide();
            } else {
                // Set a timeout to see if the user has entered the tooltip
                // content after moving away from the component the directive
                // is tied to. (This makes it so the few ms time between leaving
                // the tooltip icon and entering the tooltip content doesn't
                // cause the tooltip content to close)
                window.setTimeout(() => {
                    if (!this.tooltipContentFocusEntered) {
                        tooltip.hide();
                    }
                }, 250);
            }
        }
    }

    private handleTooltipContentFocusEnter() {
        this.tooltipContentFocusEntered = true;
    }

    private handleTooltipContentFocusLeave() {
        const tooltip = this.tooltipContent as TooltipContentComponent;
        if (this.tooltipContentFocusEntered) {
            this.tooltipContentFocusEntered = false;
        }
        tooltip.hide();
    }
}
